import React, { useRef, useState } from 'react';
import { brand } from '../../../shared/config/brand';
import { SOLUTIONS_LINKEDIN_URL, solutionsAudit } from '../../../shared/config/solutionsContent';
import { trackEvent } from '../../../shared/analytics';
import {
  submitLead,
  type AuditFormSurface,
  type LeadErrorReason,
} from '../../../services/leadsService';

export const DELIVERY_AUDIT_SECTION_ID = 'delivery-audit';
export const AUDIT_FORM_ID = 'audit-form';

const AUDIT_MAILTO = `mailto:${brand.salesEmail}?subject=Free%20Delivery%20Audit%20request&body=Work%20email%3A%0AName%3A%0ARole%20(optional)%3A%0ACompany%20(optional)%3A`;

type DeliveryAuditFormProps = {
  surface: AuditFormSurface;
  emailInputRef?: React.Ref<HTMLInputElement>;
  idPrefix?: string;
};

export function scrollToDeliveryAudit(emailInput: HTMLInputElement | null): void {
  const target =
    document.getElementById(DELIVERY_AUDIT_SECTION_ID) || document.getElementById(AUDIT_FORM_ID);
  if (!target) return;

  let settled = false;
  const detach: Array<() => void> = [];
  const focusEmail = () => {
    if (settled) return;
    settled = true;
    detach.forEach((fn) => fn());
    emailInput?.focus({ preventScroll: true });
  };

  if ('onscrollend' in window) {
    window.addEventListener('scrollend', focusEmail, { once: true });
    detach.push(() => window.removeEventListener('scrollend', focusEmail));
  } else {
    const io = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) focusEmail();
    });
    io.observe(target);
    detach.push(() => io.disconnect());
  }
  const safety = window.setTimeout(focusEmail, 1200);
  detach.push(() => window.clearTimeout(safety));

  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function DeliveryAuditForm({ surface, emailInputRef, idPrefix = 'audit' }: DeliveryAuditFormProps) {
  const formStartedRef = useRef(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [company, setCompany] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trackFormStart = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    trackEvent('solutions_form_start', { surface });
  };

  const trackError = (status: number | null, code: string | null, reason: LeadErrorReason) => {
    trackEvent('solutions_form_error', { surface, status, code, reason });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    trackEvent('solutions_form_submit', { surface });
    if (!email.trim() || !name.trim()) {
      setError('Enter your work email and name.');
      trackError(null, null, 'validation');
      return;
    }
    setSubmitting(true);
    setError(null);
    const result = await submitLead({
      email: email.trim(),
      name: name.trim(),
      role: role.trim(),
      company: company.trim(),
      website: honeypot,
      source: 'solutions-audit',
    });
    if (result.ok) {
      setSubmitted(true);
      trackEvent('solutions_form_success', { surface });
    } else {
      console.warn('[solutions] lead submit failed:', JSON.stringify(result, null, 2));
      setError('Could not send the request. Check your connection and try again.');
      trackError(result.status, result.code, result.reason);
    }
    setSubmitting(false);
  };

  const canSubmit = !!email.trim() && !!name.trim() && !submitting;
  const emailId = `${idPrefix}-email`;
  const nameId = `${idPrefix}-name`;
  const roleId = `${idPrefix}-role`;
  const companyId = `${idPrefix}-company`;
  const websiteId = `${idPrefix}-website`;

  if (submitted) {
    return (
      <div className="solutions-cta-console__output" role="status">
        <p className="solutions-cta-console__output-ok">✓ Request received.</p>
        <p className="solutions-cta-console__output-note">
          Expect a reply within one business day.
        </p>
      </div>
    );
  }

  return (
    <form className="solutions-cta-console__form" onSubmit={handleSubmit} noValidate>
      <div className="solutions-cta-console__field">
        <label className="solutions-cta-console__prompt" htmlFor={emailId}>
          Work email
        </label>
        <input
          ref={emailInputRef}
          id={emailId}
          className="solutions-cta-console__input"
          type="email"
          placeholder="name@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onFocus={trackFormStart}
          required
          autoComplete="email"
        />
      </div>
      <div className="solutions-cta-console__field">
        <label className="solutions-cta-console__prompt" htmlFor={nameId}>
          Your name
        </label>
        <input
          id={nameId}
          className="solutions-cta-console__input"
          type="text"
          placeholder="How should we address you?"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onFocus={trackFormStart}
          required
          autoComplete="name"
        />
      </div>
      <div className="solutions-cta-console__field">
        <label className="solutions-cta-console__prompt" htmlFor={roleId}>
          Role <span className="solutions-cta-console__optional">(optional)</span>
        </label>
        <input
          id={roleId}
          className="solutions-cta-console__input"
          type="text"
          placeholder="CPO, PMO, Head of Product, Delivery Lead, PO, PM…"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          autoComplete="organization-title"
        />
      </div>
      <div className="solutions-cta-console__field">
        <label className="solutions-cta-console__prompt" htmlFor={companyId}>
          Company <span className="solutions-cta-console__optional">(optional)</span>
        </label>
        <input
          id={companyId}
          className="solutions-cta-console__input"
          type="text"
          placeholder="Organization / company name"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          autoComplete="organization"
        />
      </div>

      <div className="solutions-cta-console__hp" aria-hidden="true">
        <label htmlFor={websiteId}>Website</label>
        <input
          id={websiteId}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      <p className="solutions-cta-console__hint">{solutionsAudit.replyLine}</p>
      <div className="solutions-cta-console__actions">
        <button
          className="btn-primary-large solutions-cta-console__submit"
          type="submit"
          disabled={!canSubmit}
        >
          {submitting ? <span className="cta-spinner" /> : null}
          {submitting ? 'Sending…' : solutionsAudit.submitLabel}
        </button>
      </div>
      <p className="solutions-cta-console__error" role="alert" aria-live="polite">
        {error}
      </p>
      <ul className="solutions-cta-console__trust" aria-label="What you get">
        {solutionsAudit.deliverables.map((item) => (
          <li key={item}>
            <span className="solutions-cta-console__check" aria-hidden="true">
              ✓
            </span>
            {item}
          </li>
        ))}
      </ul>
      <p className="solutions-cta-console__privacy">{solutionsAudit.privacyLine}</p>
    </form>
  );
}

type DeliveryAuditSectionProps = {
  surface: AuditFormSurface;
  emailInputRef?: React.Ref<HTMLInputElement>;
};

export function DeliveryAuditSection({ surface, emailInputRef }: DeliveryAuditSectionProps) {
  const idPrefix = surface === 'auth_signup' ? 'auth-signup' : 'audit';
  return (
    <section
      id={DELIVERY_AUDIT_SECTION_ID}
      className="solutions-cta-console animate-scale-in"
      aria-label="Request a delivery audit"
    >
      <div id={AUDIT_FORM_ID}>
        <div className="solutions-cta-console__glow" aria-hidden="true" />
        <div className="cta-content solutions-cta-console__inner">
          <header className="solutions-cta-console__header">
            <h2>{solutionsAudit.heading}</h2>
            <p className="solutions-cta-console__sub">{solutionsAudit.sub}</p>
          </header>

          <div className="solutions-cta-console__panel product-window">
            <div className="solutions-cta-console__panel-head">
              <span
                className="product-window__chip product-window__chip--static"
                aria-hidden="true"
              >
                relix — audit console
              </span>
            </div>
            <DeliveryAuditForm
              surface={surface}
              emailInputRef={emailInputRef}
              idPrefix={idPrefix}
            />
          </div>

          <p className="solutions-cta-console__alt">
            {solutionsAudit.linkedinAlt}{' '}
            <a
              href={SOLUTIONS_LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('solutions_linkedin_click')}
            >
              {solutionsAudit.linkedinCta}
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}

/** No-JS / prerender twin: mailto instead of POST so email never lands in the URL. */
export function DeliveryAuditSectionStatic() {
  return (
    <section
      id={DELIVERY_AUDIT_SECTION_ID}
      className="solutions-cta-console animate-scale-in"
      aria-label="Request a delivery audit"
    >
      <div id={AUDIT_FORM_ID}>
        <div className="solutions-cta-console__glow" aria-hidden="true" />
        <div className="cta-content solutions-cta-console__inner">
          <header className="solutions-cta-console__header">
            <h2>{solutionsAudit.heading}</h2>
            <p className="solutions-cta-console__sub">{solutionsAudit.sub}</p>
          </header>

          <div className="solutions-cta-console__panel product-window">
            <div className="solutions-cta-console__panel-head">
              <span
                className="product-window__chip product-window__chip--static"
                aria-hidden="true"
              >
                relix — audit console
              </span>
            </div>

            <div className="solutions-cta-console__form">
              <div className="solutions-cta-console__field">
                <span className="solutions-cta-console__prompt">Work email</span>
                <span className="solutions-cta-console__input solutions-cta-console__input--ghost">
                  name@company.com
                </span>
              </div>
              <div className="solutions-cta-console__field">
                <span className="solutions-cta-console__prompt">Your name</span>
                <span className="solutions-cta-console__input solutions-cta-console__input--ghost">
                  How should we address you?
                </span>
              </div>
              <p className="solutions-cta-console__hint">{solutionsAudit.replyLine}</p>
              <div className="solutions-cta-console__actions">
                <a className="btn-primary-large solutions-cta-console__submit" href={AUDIT_MAILTO}>
                  {solutionsAudit.submitLabel}
                </a>
              </div>
              <ul className="solutions-cta-console__trust" aria-label="What you get">
                {solutionsAudit.deliverables.map((item) => (
                  <li key={item}>
                    <span className="solutions-cta-console__check" aria-hidden="true">
                      ✓
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="solutions-cta-console__privacy">{solutionsAudit.privacyLine}</p>
            </div>
          </div>

          <p className="solutions-cta-console__alt">
            {solutionsAudit.linkedinAlt}{' '}
            <a href={SOLUTIONS_LINKEDIN_URL} target="_blank" rel="noopener noreferrer">
              {solutionsAudit.linkedinCta}
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
